/**
 * BugMail Client
 * Core class for error tracking and reporting
 */

import { BugMailCoreClient } from '@bugmail-js/core/bugmail-core-client';
import { CoreNetworkManager } from '@bugmail-js/core/network-manager';
import NetworkManager from './network-manager';
import { CoreErrorProcessor } from '@bugmail-js/core/error-processor';
import { CorePluginManager } from '@bugmail-js/core/plugin-manager';
import { generateErrorFingerprint } from '@bugmail-js/core/error-grouping';
import { parseStackTrace } from '@bugmail-js/core/source-map';
import ContextCollector from './context-collector';
import ErrorFilter from './error-filter';
import AIAnalysisService from './services/ai-analysis';
import { version } from '../package.json';

class BugMailClient extends BugMailCoreClient {
  /**
   * Initialize the BugMail client
   * @param {Object} config - Configuration options
   * @param {string} config.apiKey - Project API key
   * @param {string} [config.endpoint] - API endpoint URL
   * @param {boolean} [config.captureUnhandledErrors=true] - Automatically capture unhandled errors
   * @param {boolean} [config.captureUnhandledRejections=true] - Automatically capture unhandled promise rejections
   * @param {number} [config.maxBreadcrumbs=100] - Maximum number of breadcrumbs to collect
   * @param {boolean} [config.captureConsoleErrors=true] - Capture console.error calls
   * @param {string} [config.projectId] - The project ID (if not using API key lookup)
   * @param {Array} [config.plugins] - Array of plugins to register
   */
  constructor(config = {}) {
    super(config);

    const isProd = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') ||
      (typeof window !== 'undefined' && window.BUGMAIL_CONFIG?.environment === 'production');

    const resolvedEndpoint = config.endpoint ||
      (typeof process !== 'undefined' ? (process.env?.BUGMAIL_API_BASE_URL || process.env?.API_BASE_URL || process.env?.BUGMAIL_API_URL) : '') ||
      '';

    if (isProd && !resolvedEndpoint) {
      console.warn('[BugMail] MISSING API endpoint in production. Errors might not be reported to the correct server.');
    }
    this.config = {
      ...this.config,
      endpoint: resolvedEndpoint,
      captureUnhandledErrors: config.captureUnhandledErrors !== false,
      captureUnhandledRejections: config.captureUnhandledRejections !== false,
      maxBreadcrumbs: config.maxBreadcrumbs || 100,
      captureConsoleErrors: config.captureConsoleErrors !== false,
      release: config.release || 'development',
      environment: config.environment || 'development',
      sampleRate: typeof config.sampleRate === 'number' ? config.sampleRate : 1.0,
      projectId: config.projectId,
      debug: !!config.debug,
      enableAIAnalysis: true,
      aiAnalysisConfig: {
        cacheTimeout: 24 * 60 * 60 * 1000, // 24 hours
        minErrorsToAnalyze: 1, // Minimum occurrences before triggering analysis
      },
      ...config
    };

    // Create dependent components
    this.pluginManager = new CorePluginManager();
    // Use the browser-specific NetworkManager (handles window, offline, retries)
    this.network = new NetworkManager(this.config);
    this.contextCollector = new ContextCollector(this.config);
    this.errorProcessor = new CoreErrorProcessor();
    this.errorFilter = new ErrorFilter(this.config);

    // Initialize breadcrumbs
    this.breadcrumbs = [];
    this.originalConsoleError = null;

    // Initialize error tracking
    this.errorGroups = new Map();
    this.errorCounts = new Map();

    // Bind methods to maintain context
    this.handleError = this.handleError.bind(this);
    this.handleRejection = this.handleRejection.bind(this);
    this.captureError = this.captureError.bind(this);
    this.addBreadcrumb = this.addBreadcrumb.bind(this);

    // Set up navigation and click tracking
    this.setupNavigationTracking();
    this.setupClickTracking();

    // Optionally wrap EventTarget listeners to capture handler exceptions
    if (this.config.wrapEventListeners !== false) {
      this._installEventListenerWrapper();
    }

    // Register plugins if provided
    if (Array.isArray(config.plugins)) {
      config.plugins.forEach(plugin => this.registerPlugin(plugin));
    }

    // Run setup hooks (defensive: pluginManager may be a plain object in some builds)
    if (this.pluginManager && typeof this.pluginManager.runHooks === 'function') {
      this.pluginManager.runHooks('setup', undefined, this);
    } else {
      if (this.config.debug) {
        console.warn('[BugMail] No plugin manager available to run setup hooks.');
      }
    }

    // Set up error listeners if auto-capture is enabled
    this.setupErrorListeners();

    // Capture initial context
    this.contextCollector.captureInitialContext();

    // Initialize AI Analysis service if enabled
    if (this.config.enableAIAnalysis) {
      this.aiService = new AIAnalysisService({
        endpoint: this.config.endpoint,
        apiKey: this.config.apiKey
      });

      // Set cache timeout
      if (this.aiService && typeof this.aiService.setMaxCacheAge === 'function') {
        this.aiService.setMaxCacheAge(this.config.aiAnalysisConfig.cacheTimeout);
      }
    }
  }

  /**
   * Wrap EventTarget.prototype.addEventListener so exceptions from handlers
   * are captured by the SDK. Uses a WeakMap to map originals to wrapped fns.
   * This is defensive and opt-out via config.wrapEventListeners = false.
   * @private
   */
  _installEventListenerWrapper() {
    try {
      if (typeof EventTarget === 'undefined') return;
      if (this.__bugmail_event_wrapper_installed) return;

      const client = this;
      const origAdd = EventTarget.prototype.addEventListener;
      const origRemove = EventTarget.prototype.removeEventListener;
      const wrappedMap = new WeakMap();

      EventTarget.prototype.addEventListener = function (type, listener, options) {
        if (typeof listener === 'function') {
          const existing = wrappedMap.get(listener);
          if (existing) return origAdd.call(this, type, existing, options);

          const wrapped = function (...args) {
            try {
              return listener.apply(this, args);
            } catch (err) {
              try {
                // Best-effort capture; don't swallow original error
                client.captureError(err, { payload: { source: 'event-listener', eventType: type } });
              } catch (e) {
                // ignore
              }
              throw err;
            }
          };
          wrappedMap.set(listener, wrapped);
          return origAdd.call(this, type, wrapped, options);
        }
        return origAdd.call(this, type, listener, options);
      };

      EventTarget.prototype.removeEventListener = function (type, listener, options) {
        if (typeof listener === 'function') {
          const wrapped = wrappedMap.get(listener);
          if (wrapped) {
            wrappedMap.delete(listener);
            return origRemove.call(this, type, wrapped, options);
          }
        }
        return origRemove.call(this, type, listener, options);
      };

      this.__bugmail_event_wrapper_installed = true;
    } catch (e) {
      // silently ignore if environment doesn't allow modification
      if (this.config.debug) console.warn('[BugMail] Failed to install event listener wrapper:', e);
    }
  }

  /**
   * Set up error listeners
   * @private
   */
  setupErrorListeners() {
    if (typeof window === 'undefined') return;

    // Capture unhandled errors
    if (this.config.captureUnhandledErrors) {
      window.addEventListener('error', this.handleError);
    }

    // Capture unhandled promise rejections
    if (this.config.captureUnhandledRejections) {
      window.addEventListener('unhandledrejection', this.handleRejection);
    }

    // Capture console.error calls
    if (this.config.captureConsoleErrors) {
      this.setupConsoleCapture();
    }
  }

  /**
   * Handle window error event
   * @param {ErrorEvent} event - Error event
   * @private
   */
  handleError(event) {
    // Use ErrorFilter to check if we should capture this error
    if (!this.errorFilter.shouldCaptureError({
      message: event.message,
      stack: event.error?.stack,
      filename: event.filename
    }, { isInternalError: this._isInternalError })) {
      return;
    }

    const errorData = {
      type: 'error',
      message: event.message || 'Unknown error',
      stack: event.error ? event.error.stack : null,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
    };

    // Directly capture the error. Recursion protection is handled within captureError
    this.captureError(errorData);
  }

  /**
   * Handle unhandled promise rejection
   * @param {PromiseRejectionEvent} event - Promise rejection event
   * @private
   */
  handleRejection(event) {
    let error = event.reason;
    let message = 'Unhandled Promise Rejection';
    let stack = null;

    if (error instanceof Error) {
      message = error.message;
      stack = error.stack;
    } else if (typeof error === 'string') {
      message = error;
    } else if (typeof error === 'object') {
      message = JSON.stringify(error);
    }

    // Use ErrorFilter to check if we should capture this error
    if (!this.errorFilter.shouldCaptureError({
      message,
      stack
    }, { isInternalError: this._isInternalError })) {
      return;
    }

    const errorData = {
      type: 'unhandledrejection',
      message,
      stack,
      timestamp: new Date().toISOString(),
    };

    this.captureError(errorData);
  }

  /**
   * Set up console.error capturing
   * @private
   */
  setupConsoleCapture() {
    if (typeof console === 'undefined') return;

    // Store original console.error
    this.originalConsoleError = console.error;

    // Replace console.error with our version
    console.error = (...args) => {
      // Check if should capture before processing
      if (this.errorFilter.shouldCaptureConsoleError(args)) {
        const error = args[0];
        if (error instanceof Error) {
          this.captureError(error, { source: 'console.error' });
        } else {
          this.captureError({
            type: 'console.error',
            message: args.map(arg =>
              typeof arg === 'string' ? arg : JSON.stringify(arg)
            ).join(' '),
            timestamp: new Date().toISOString()
          });
        }
      }

      // Always call original console.error
      this.originalConsoleError.apply(console, args);
    };
  }

  /**
   * Automatically capture navigation events as breadcrumbs
   * @private
   */
  setupNavigationTracking() {
    if (typeof window === 'undefined') return;

    const addNavigationBreadcrumb = (type, data = {}) => {
      this.addBreadcrumb({
        type: 'navigation',
        message: `${type}: ${window.location.href}`,
        data: {
          ...data,
          from: document.referrer,
          to: window.location.href
        }
      });
    };

    // Track initial page load
    addNavigationBreadcrumb('load');

    // Track history changes
    window.addEventListener('popstate', () => {
      addNavigationBreadcrumb('popstate');
    });

    // Track hash changes
    window.addEventListener('hashchange', () => {
      addNavigationBreadcrumb('hashchange');
    });
  }

  /**
   * Setup automatic click tracking for breadcrumbs
   * @private
   */
  setupClickTracking() {
    if (typeof window === 'undefined') return;

    window.addEventListener('click', event => {
      const target = event.target;
      const tagName = target.tagName.toLowerCase();

      // Only track clicks on interactive elements
      if (!['a', 'button', 'input', 'select', 'textarea'].includes(tagName)) {
        return;
      }

      this.addBreadcrumb({
        type: 'ui.click',
        message: `Clicked ${tagName} element`,
        data: {
          tagName,
          id: target.id,
          className: target.className,
          text: target.textContent?.trim()?.substring(0, 50)
        }
      });
    });
  }

  /**
   * Add a breadcrumb to track user actions
   * @param {Object} breadcrumb - Breadcrumb data
   * @param {string} breadcrumb.type - Type of breadcrumb (navigation, request, user, etc.)
   * @param {string} breadcrumb.message - Breadcrumb message
   * @param {string} [breadcrumb.level='info'] - Severity level (info, warning, error)
   * @param {Object} [breadcrumb.data] - Additional data
   * @public
   */
  addBreadcrumb(breadcrumb) {
    // Ensure required fields
    if (!breadcrumb.message) {
      console.warn('BugMail: Breadcrumb must have a message');
      return;
    }

    const crumb = {
      timestamp: new Date().toISOString(),
      type: breadcrumb.type || 'default',
      message: breadcrumb.message,
      level: breadcrumb.level || 'info',
      data: breadcrumb.data || {}
    };

    this.breadcrumbs.push(crumb);

    // Remove old breadcrumbs if over limit
    if (this.breadcrumbs.length > this.config.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }

    if (this.config.debug) {
      console.log('BugMail: Added breadcrumb', crumb);
    }
  }

  /**
   * Clear all breadcrumbs
   * @public
   */
  clearBreadcrumbs() {
    this.breadcrumbs = [];

    if (this.config.debug) {
      console.log('BugMail: Cleared all breadcrumbs');
    }
  }

  /**
   * Set user information
   * @param {Object} user - User data
   * @param {string} [user.id] - User ID
   * @param {string} [user.email] - User email
   * @param {string} [user.username] - Username
   * @public
   */
  setUser(user) {
    this.contextCollector.setUser(user);
  }

  /**
   * Clear user context (e.g., on logout)
   * @public
   */
  clearUser() {
    this.contextCollector.clearUser();
  }

  /**
   * Set custom context data
   * @param {Object} data - Custom context data
   * @public
   */
  setContext(data) {
    this.contextCollector.setCustomContext(data);
  }

  /**
   * Register a plugin
   * @param {Object} plugin - Plugin instance
   * @public
   */
  registerPlugin(plugin) {
    this.pluginManager.register(plugin);
    plugin.setup?.(this);
  }

  /**
   * Capture an error and optionally get AI analysis
   * @param {Error} error - The error to capture
   * @param {Object} [context] - Additional context
   * @returns {Promise<Object>} Captured error data with optional AI analysis
   */
  async captureError(error, context = {}) {
    // Don't process if we're handling an internal error
    if (this._isInternalError) {
      return;
    }

    // Filter check - don't capture SDK errors
    if (!this.errorFilter.shouldCaptureError(error, {
      isInternalError: this._isInternalError
    })) {
      if (this.config.debug) {
        console.log('[BugMail] Filtered out SDK error:', error.message || error);
      }
      return;
    }

    // Generate error fingerprint for grouping
    const fingerprint = generateErrorFingerprint(error);

    // Track error occurrence and group
    if (!this.errorGroups.has(fingerprint)) {
      this.errorGroups.set(fingerprint, {
        firstSeen: new Date().toISOString(),
        count: 0,
        lastSeen: null,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: error.cause
        }
      });
    }

    const group = this.errorGroups.get(fingerprint);
    group.count++;
    group.lastSeen = new Date().toISOString();

    // Get basic error data
    const errorData = {
      error: {
        name: error.name || 'UnknownError', // Ensure name is always present
        message: error.message || 'No message provided',
        stack: error.stack || 'No stack trace available', // Ensure stack is always present
        cause: error.cause
      },
      context: {
        ...this.contextCollector.collectContext(),
        ...context,
        breadcrumbs: this.breadcrumbs
      },
      timestamp: new Date().toISOString(),
      groupInfo: {
        fingerprint,
        occurence: group.count,
        firstSeen: group.firstSeen,
        lastSeen: group.lastSeen
      },
      // Add other relevant config/context
      release: this.config.release,
      environment: this.config.environment,
      sdk: {
        name: 'bugmail-js',
        version: version
      }
    };

    // Check if we should perform AI analysis
    if (this.config.enableAIAnalysis &&
      this.aiService &&
      group.count >= this.config.aiAnalysisConfig.minErrorsToAnalyze) {
      try {
        const analysis = await this.aiService.analyzeError(error, {
          ...errorData.context,
          occurences: group.count
        });
        if (analysis) {
          errorData.aiAnalysis = analysis;
        }
      } catch (analysisError) {
        console.warn('[BugMail] AI Analysis failed:', analysisError);
      }
    }

    // Send to BugMail API using NetworkManager
    try {
      this._isInternalError = true; // Set flag before making request

      // *** Use NetworkManager to send the error ***
      const response = await this.network.sendError(errorData);

      if (this.config.debug) {
        console.log('[BugMail] Error submission initiated:', response); // Log NetworkManager response
      }

      // Return the original error data, as NetworkManager handles async sending/retries
      return errorData;

    } catch (apiError) {
      // NetworkManager handles its own errors internally (retry/offline)
      // We might log here if sendError itself throws an unexpected error
      console.warn('[BugMail] Failed to initiate error sending via NetworkManager:', apiError);
      return errorData; // Still return the data that was attempted
    } finally {
      this._isInternalError = false; // Always reset the flag
    }
  }

  /**
   * Get error groups statistics
   * @returns {Object} Error group statistics
   * @public
   */
  getErrorStats() {
    const stats = {
      total: 0,
      groups: this.errorGroups.size,
      topErrors: []
    };

    // Convert groups to array for sorting
    const groups = Array.from(this.errorGroups.entries())
      .map(([fingerprint, data]) => ({
        fingerprint,
        ...data
      }))
      .sort((a, b) => b.count - a.count);

    stats.total = groups.reduce((sum, group) => sum + group.count, 0);
    stats.topErrors = groups.slice(0, 5); // Top 5 most frequent errors

    return stats;
  }

  /**
   * Clear error tracking data
   * @public
   */
  clearErrors() {
    this.errorGroups.clear();
    this.errorCounts.clear();
  }

  /**
   * Clear AI analysis cache
   * @public
   */
  clearAICache() {
    if (this.aiService) {
      this.aiService.clearCache();
    }
  }

  /**
   * Disconnect and cleanup
   * @public
   */
  disconnect() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('error', this.handleError);
      window.removeEventListener('unhandledrejection', this.handleRejection);
    }

    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }

    // Clear AI cache
    this.clearAICache();

    // Clear error tracking
    this.clearErrors();

    if (this.pluginManager && typeof this.pluginManager.runHooks === 'function') {
      this.pluginManager.runHooks('disconnect');
    } else if (this.config.debug) {
      console.warn('[BugMail] No plugin manager available to run disconnect hooks.');
    }
  }
}

// Export BugMailClient class and version
export { version };
export default BugMailClient;
